import type { OrderFormLine, OrderFormTaxConfig, TaxConfigItem } from './types';

export interface ProductTaxInfo {
  taxId?: string;
  taxRate: number;
  taxName?: string;
}

export function resolveProductTaxInfo(
  product: OrderFormLine,
  taxConfig: OrderFormTaxConfig
): ProductTaxInfo {
  const allTaxes = taxConfig.taxes ?? [];
  const taxes = allTaxes.filter(tax => tax.enabled);
  const taxesMap = new Map(taxes.map(tax => [tax.id, tax]));

  const configuredPrimaryTaxId =
    taxConfig.primaryTaxId && taxesMap.has(taxConfig.primaryTaxId)
      ? taxConfig.primaryTaxId
      : taxesMap.has('iva-general')
        ? 'iva-general'
        : taxes[0]?.id;

  const effectiveTaxId =
    product.customTaxId && taxesMap.has(product.customTaxId)
      ? product.customTaxId
      : product.taxId && taxesMap.has(product.taxId)
        ? product.taxId
        : configuredPrimaryTaxId;

  const fallbackRate =
    taxes.length === 0 && typeof taxConfig.iva === 'number'
      ? taxConfig.iva
      : 0;
  const taxRate = effectiveTaxId
    ? (taxesMap.get(effectiveTaxId)?.rate ?? fallbackRate)
    : fallbackRate;

  return {
    taxId: effectiveTaxId,
    taxRate,
    taxName: effectiveTaxId ? taxesMap.get(effectiveTaxId)?.name : undefined,
  };
}

/**
 * Line tax persisted on the sale. With taxes off, totals use 0% but we still persist an exempt
 * fiscal taxId when the org defines one.
 */
export function resolveOrderLineTaxForPersistence(
  product: OrderFormLine,
  taxConfig: OrderFormTaxConfig,
  applyIva: boolean
): { taxId?: string; taxRate: number } {
  const info = resolveProductTaxInfo(product, taxConfig);
  if (applyIva) {
    return { taxId: info.taxId, taxRate: info.taxRate };
  }
  const enabled = (taxConfig.taxes ?? []).filter((t: TaxConfigItem) => t.enabled);
  const byId = new Map(enabled.map(t => [t.id, t]));
  let exemptTaxId: string | undefined;
  if (byId.has('tax-exempt')) {
    exemptTaxId = 'tax-exempt';
  } else {
    const zeroRate = enabled.find(
      t => Number.isFinite(t.rate) && Number(t.rate) === 0
    );
    exemptTaxId = zeroRate?.id;
  }
  return { taxId: exemptTaxId, taxRate: 0 };
}
